type BookSource = 'BUY_NEW_OFFLINE'
    | 'BUY_NEW_ONLINE'
    | 'BUY_USED_OFFLINE'
    | 'BUY_USED_ONLINE'
    | 'LIBRARY'
    | 'BORROW_STORE'
    | 'BORROW_FRIENDS'
    | 'SUBSCRIPTION'
    | 'OTHERS'
    | 'NOT_PROVIDED'

type BookForm = 'PHYSICAL'
    | 'EBOOK'
    | 'AUDIO'

const BOOK_LANGUAGES = [
    'KOREAN',
    'ENGLISH',
    'JAPANESE',
    'CHINESE',
    'FRENCH',
    'SPANISH',
    'GERMAN',
    'ITALIAN',
    'RUSSIAN',
    'ARABIC',
    'TURKISH',
    'CROATIAN',
    'URDU',
    'PORTUGUESE',
    'ROMANIAN',
    'POLISH',
    'DUTCH',
    'SWEDISH',
    'NORWEGIAN',
    'GREEK',
    'PERSIAN',
    'HEBREW',
    'HINDI',
    'INDONESIAN',
    'CZECH',
    'HUNGARIAN',
    'DANISH',
    'FINNISH',
    'VIETNAMESE',
    'LATIN',
    'UNKNOWN',
] as const

type BookLanguage = typeof BOOK_LANGUAGES[number]

const getBookLanguageDisplayName = (language: BookLanguage): string => {
    switch (language) {
        case 'KOREAN':
            return '한국어';
        case 'ENGLISH':
            return '영어';
        case 'JAPANESE':
            return '일본어';
        case 'CHINESE':
            return '중국어';
        case 'FRENCH':
            return '프랑스어';
        case 'SPANISH':
            return '스페인어';
        case 'GERMAN':
            return '독일어';
        case 'ITALIAN':
            return '이탈리아어';
        case 'RUSSIAN':
            return '러시아어';
        case 'ARABIC':
            return '아랍어';
        case 'TURKISH':
            return '튀르키예어';
        case 'CROATIAN':
            return '크로아티아어';
        case 'URDU':
            return '우르두어';
        case 'PORTUGUESE':
            return '포르투갈어';
        case 'ROMANIAN':
            return '루마니아어';
        case 'POLISH':
            return '폴란드어';
        case 'DUTCH':
            return '네덜란드어';
        case 'SWEDISH':
            return '스웨덴어';
        case 'NORWEGIAN':
            return '노르웨이어';
        case 'GREEK':
            return '그리스어';
        case 'PERSIAN':
            return '페르시아어';
        case 'HEBREW':
            return '히브리어';
        case 'HINDI':
            return '힌디어';
        case 'INDONESIAN':
            return '인도네시아어';
        case 'CZECH':
            return '체코어';
        case 'HUNGARIAN':
            return '헝가리어';
        case 'DANISH':
            return '덴마크어';
        case 'FINNISH':
            return '핀란드어';
        case 'VIETNAMESE':
            return '베트남어';
        case 'LATIN':
            return '라틴어';
        default:
            return '기타 언어';
    }
}

const bookLanguageFlagMap: Record<BookLanguage, string> = {
    KOREAN: '🇰🇷',
    ENGLISH: '🇺🇸',
    JAPANESE: '🇯🇵',
    CHINESE: '🇨🇳',
    FRENCH: '🇫🇷',
    SPANISH: '🇪🇸',
    GERMAN: '🇩🇪',
    ITALIAN: '🇮🇹',
    RUSSIAN: '🇷🇺',
    ARABIC: '🇸🇦',
    TURKISH: '🇹🇷',
    CROATIAN: '🇭🇷',
    URDU: '🇵🇰',
    PORTUGUESE: '🇵🇹',
    ROMANIAN: '🇷🇴',
    POLISH: '🇵🇱',
    DUTCH: '🇳🇱',
    SWEDISH: '🇸🇪',
    NORWEGIAN: '🇳🇴',
    GREEK: '🇬🇷',
    PERSIAN: '🇮🇷',
    HEBREW: '🇮🇱',
    HINDI: '🇮🇳',
    INDONESIAN: '🇮🇩',
    CZECH: '🇨🇿',
    HUNGARIAN: '🇭🇺',
    DANISH: '🇩🇰',
    FINNISH: '🇫🇮',
    VIETNAMESE: '🇻🇳',
    LATIN: '🇻🇦',
    UNKNOWN: '🌐',
}

const getBookLanguageFlag = (language: BookLanguage): string => bookLanguageFlagMap[language] ?? '🌐'

type BookStatus = 'NOT_ADDED'
    | 'NOT_STARTED'
    | 'READING'
    | 'GIVE_UP'
    | 'DONE'

type BookCategory = 'OTHERS' |
    'PHILOSOPHY' |
    'RELIGION' |
    'SOCIAL_SCIENCE' |
    'NATURAL_SCIENCE' |
    'TECHNOLOGY' |
    'ART' |
    'LANGUAGE' |
    'LITERATURE' |
    'HISTORY' |
    'UNKNOWN' |
    'MANUAL'

interface BookPublisher {
    urlName: string | null
    name: string
    logoImageUrl: string | null
    homePageUrl: string | null
}

const getBookSourceDisplayName = (source: BookSource): string => {
    switch (source) {
        case 'BUY_NEW_OFFLINE':
            return '새 책 (오프라인)'
        case 'BUY_NEW_ONLINE':
            return '새 책 (온라인)'
        case 'BUY_USED_OFFLINE':
            return '중고 (오프라인)'
        case 'BUY_USED_ONLINE':
            return '중고 (온라인)'
        case 'LIBRARY':
            return '도서관'
        case 'BORROW_STORE':
            return '대여(서점)'
        case 'BORROW_FRIENDS':
            return '대여(친구)'
        case 'SUBSCRIPTION':
            return '구독'
        case 'OTHERS':
            return '기타'
        case 'NOT_PROVIDED':
            return ''
    }
}

const getBookSourceShortDisplayName = (source: BookSource): string => {
    switch (source) {
        case 'BUY_NEW_OFFLINE':
        case 'BUY_NEW_ONLINE':
            return '구입'
        case 'BUY_USED_OFFLINE':
        case 'BUY_USED_ONLINE':
            return '중고'
        case 'LIBRARY':
            return '도서관'
        case 'BORROW_STORE':
            return '대여'
        case 'BORROW_FRIENDS':
            return '대여'
        case 'SUBSCRIPTION':
            return '구독'
        case 'OTHERS':
            return '기타'
        case 'NOT_PROVIDED':
            return ''
    }
}

export type {BookSource, BookForm, BookLanguage, BookStatus, BookCategory, BookPublisher}
export {BOOK_LANGUAGES, getBookLanguageDisplayName, getBookLanguageFlag, getBookSourceDisplayName, getBookSourceShortDisplayName}